import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Quote } from "lucide-react";
import { ParticleField } from "@/components/site/ParticleField";
import { AIAssistant } from "@/components/site/AIAssistant";
import { Reveal, Stagger, StaggerItem, MaskLines, Counter } from "@/components/site/motion";
import { Eyebrow, Icon, Marquee } from "@/components/site/ui";
import { StrategyDashboard } from "@/components/site/StrategyDashboard";
import { VeilleStrategique } from "@/components/site/VeilleStrategique";
import { STATS, PILLARS, PARTNERS, PROFILES, TESTIMONIALS, IMAGES } from "@/data/content";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden bg-night" data-testid="hero">
      <motion.div style={{ y }} className="absolute inset-0">
        <img src={IMAGES.heroAbidjan} alt="Skyline d'Abidjan, Côte d'Ivoire" className="h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-b from-night/70 via-night/85 to-night" />
      </motion.div>
      <ParticleField className="absolute inset-0 h-full w-full" density={70} />

      <motion.div style={{ opacity }} className="relative mx-auto max-w-7xl px-5 sm:px-8 w-full pt-28 pb-20">
        <h1 className="mt-6 font-display text-4xl sm:text-6xl lg:text-[5.2rem] font-medium tracking-tight text-white leading-[1.03] max-w-5xl">
          <MaskLines lines={["Votre partenaire stratégique"]} />
          <span className="reveal-mask">
            <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}>
              présent en <em className="not-italic italic text-gold-gradient">France</em> et
            </motion.span>
          </span>
          <span className="reveal-mask">
            <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.52, ease: [0.22, 1, 0.36, 1] }}>
              aux côtés de la <em className="not-italic italic text-gold-gradient">diaspora</em>
            </motion.span>
          </span>
          <span className="reveal-mask">
            <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.64, ease: [0.22, 1, 0.36, 1] }}>
              pour agir stratégiquement partout en <em className="not-italic italic text-gold-gradient">Afrique</em>.
            </motion.span>
          </span>
        </h1>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95 }} className="mt-9 flex flex-col gap-6">
          <Link to="/expertises" data-testid="hero-cta-expertises" className="group inline-flex w-fit items-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-light px-7 py-3.5 font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5">
            Découvrir nos expertises
            <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-white/50 mb-3">✦ Assistant intelligent</p>
            <AIAssistant variant="dark" />
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2 }} className="mt-10 flex items-center gap-5 text-sm text-white/60 font-body">
          <span>🇫🇷 France & Outre-mer</span><span className="h-4 w-px bg-white/20" /><span>🇧🇯 Bénin</span><span className="h-4 w-px bg-white/20" /><span>🇨🇮 Côte d'Ivoire</span>
        </motion.div>
      </motion.div>
    </section>
  );
};

const Stats = () => (
  <section className="relative bg-gradient-to-b from-night to-cream py-20" data-testid="stats-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
        {STATS.map((s) => (
          <StaggerItem key={s.label} className="text-center lg:text-left">
            <div className="font-display text-5xl sm:text-6xl font-semibold text-gold-gradient">
              <Counter value={s.value} suffix={s.suffix} />
            </div>
            <p className="mt-2 text-sm font-body text-white/70 uppercase tracking-wide">{s.label}</p>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const History = () => (
  <section className="bg-cream py-24 sm:py-32" data-testid="history-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid lg:grid-cols-2 gap-14 lg:gap-24 items-center">
        <Reveal>
          <Eyebrow>Notre histoire</Eyebrow>
          <h2 className="mt-5 font-display text-4xl sm:text-5xl font-medium tracking-tight text-navy leading-tight">
            De la <em className="not-italic italic text-gold-gradient">France</em> à l'Afrique, une expertise sur mesure
          </h2>

          <div className="mt-10 space-y-8">
            {[
              {
                icon: "Sprout",
                t: "Notre genèse",
                d: "Notre entreprise est née de la volonté de rapprocher les expertises françaises des opportunités offertes par les territoires d'outre-mer et le continent africain. Nous accompagnons les organisations publiques et privées qui souhaitent développer leurs projets avec une approche stratégique, pragmatique et adaptée aux réalités locales.",
              },
              {
                icon: "Eye",
                t: "Notre vision",
                d: "Être un partenaire de confiance pour les entreprises, les institutions publiques et les entrepreneurs qui souhaitent créer de la valeur et développer durablement leurs activités en France, en outre-mer et en Afrique.",
              },
              {
                icon: "Compass",
                t: "Notre mission",
                d: "Accompagner nos clients dans leurs décisions stratégiques, le développement de leurs projets et leur croissance, en leur apportant une expertise de proximité, une vision internationale et une parfaite compréhension des enjeux locaux.",
              },
            ].map((b) => (
              <div key={b.t} className="flex gap-5" data-testid={`history-block-${b.t.toLowerCase().replace(/\s/g, '-')}`}>
                <span className="shrink-0 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-navy text-gold">
                  <Icon name={b.icon} size={22} />
                </span>
                <div>
                  <h3 className="font-display text-xl text-navy">{b.t}</h3>
                  <p className="mt-2 text-anthracite font-body leading-relaxed">{b.d}</p>
                </div>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="relative">
            <div className="relative overflow-hidden rounded-3xl">
              <img src={IMAGES.parisHero} alt="Paris — base européenne de Bridge Partners" className="w-full h-[460px] object-cover transition-transform duration-700 hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-night/60 to-transparent" />
              <p className="absolute bottom-5 left-5 text-white font-display text-lg">Paris · Outre-mer · Afrique</p>
            </div>
            <div className="absolute -top-6 -right-6 rounded-2xl bg-navy px-6 py-5 shadow-xl">
              <div className="font-display text-3xl text-gold-gradient font-semibold">3</div>
              <p className="text-xs text-white/70 uppercase tracking-wide">Zones d'ancrage</p>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Expertise = () => (
  <section className="bg-cream pb-24 sm:pb-32" data-testid="expertise-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="max-w-2xl">
        <Eyebrow>Nos expertises</Eyebrow>
        <h2 className="mt-5 font-display text-4xl sm:text-5xl font-medium tracking-tight text-navy">
          Quatre <em className="not-italic italic text-gold-gradient">pôles</em> d'expertise
        </h2>
      </Reveal>
      <Stagger className="mt-14 grid gap-6 md:grid-cols-2">
        {PILLARS.map((p) => (
          <StaggerItem key={p.key}>
            <Link to={`/expertises#${p.key}`} data-testid={`expertise-card-${p.num}`} className="card-lumen group relative block overflow-hidden rounded-2xl border border-navy/10 bg-white p-8 hover:border-gold h-full">
              <span className="absolute top-4 right-6 font-display text-7xl font-bold text-navy/5 select-none">{p.num}</span>
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-navy text-gold">
                <Icon name={p.icon} size={26} />
              </span>
              <h3 className="mt-6 font-display text-2xl text-navy">{p.title}</h3>
              <p className="mt-3 text-anthracite font-body leading-relaxed">{p.short}</p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-gold group-hover:gap-3 transition-all">
                Découvrir <ArrowRight size={15} />
              </span>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const Partners = () => (
  <section className="bg-night py-16 border-y border-gold/10" data-testid="partners-section">
    <p className="text-center text-xs uppercase tracking-[0.25em] text-white/40 mb-10">Ils nous font confiance</p>
    <Marquee items={PARTNERS} stroke={false} />
  </section>
);

const Profiles = () => (
  <section className="bg-cream py-24 sm:py-32" data-testid="profiles-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="max-w-2xl">
        <Eyebrow>Vous êtes</Eyebrow>
        <h2 className="mt-5 font-display text-4xl sm:text-5xl font-medium tracking-tight text-navy">
          Vous êtes l'un de ces <em className="not-italic italic text-gold-gradient">profils</em> ?
        </h2>
      </Reveal>
      <Stagger className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {PROFILES.map((p, i) => (
          <StaggerItem key={i}>
            <Link to={p.to} data-testid={`profile-card-${i}`} className="card-lumen group flex h-full flex-col justify-between rounded-2xl border border-navy/10 bg-white p-7 hover:border-gold">
              <div>
                <span className="font-display text-4xl font-bold text-gold-gradient">0{i + 1}</span>
                <h3 className="mt-4 font-display text-xl text-navy leading-snug">{p.title}</h3>
                <p className="mt-3 text-sm text-anthracite font-body">{p.desc}</p>
              </div>
              <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-gold group-hover:gap-3 transition-all">
                Vous êtes à cet endroit <ArrowRight size={14} />
              </span>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const Testimonials = () => (
  <section className="bg-cream-dark py-24 sm:py-32" data-testid="testimonials-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="max-w-2xl">
        <Eyebrow>Témoignages</Eyebrow>
        <h2 className="mt-5 font-display text-4xl sm:text-5xl font-medium tracking-tight text-navy">
          Ce que disent nos <em className="not-italic italic text-gold-gradient">clients</em>
        </h2>
      </Reveal>
      <Stagger className="mt-14 grid gap-6 md:grid-cols-2">
        {TESTIMONIALS.map((t, i) => (
          <StaggerItem key={i}>
            <div className="rounded-2xl bg-white p-8 h-full border border-navy/5" data-testid={`testimonial-${i}`}>
              <Quote size={30} className="text-gold/40" />
              <p className="mt-4 text-anthracite font-body leading-relaxed italic">“{t.quote}”</p>
              <div className="mt-6 flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-navy font-display font-semibold text-gold">{t.initials}</span>
                <div>
                  <p className="font-display text-navy">{t.name}</p>
                  <p className="text-sm text-anthracite/70 font-body">{t.role}</p>
                </div>
              </div>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const FinalCTA = () => (
  <section className="relative overflow-hidden bg-night py-28" data-testid="final-cta">
    <ParticleField className="absolute inset-0 h-full w-full" density={50} />
    <div className="absolute inset-0 bg-gradient-to-br from-navy/40 to-night" />
    <div className="relative mx-auto max-w-4xl px-5 sm:px-8 text-center">
      <Reveal>
        <h2 className="font-display text-4xl sm:text-6xl font-medium tracking-tight text-white leading-tight">
          Votre <em className="not-italic italic text-gold-gradient">ambition</em> mérite un accompagnement à la hauteur
        </h2>
        <p className="mt-6 text-lg text-white/70 font-body max-w-2xl mx-auto">
          Parlons de votre projet. Première consultation gratuite, sans engagement.
        </p>
        <div className="mt-10 flex flex-col items-center gap-8">
          <Link to="/contact" data-testid="final-cta-button" className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-light px-8 py-4 font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5">
            Démarrer un projet <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <AIAssistant variant="dark" />
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <Stats />
      <History />
      <Expertise />
      <StrategyDashboard />
      <VeilleStrategique />
      <Profiles />
      <Testimonials />
      <FinalCTA />
    </>
  );
}
